import React from 'react';
import { makeStyles, tokens, Text, Card, Title1, Title3, Badge } from '@fluentui/react-components';
import { Star24Filled, Person24Filled, Person24Regular, Mic24Regular, Play24Filled } from '@fluentui/react-icons';
import { Link } from 'react-router-dom';
import { UserMedia } from '../types';
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';
import { useScrollManager } from '../hooks/useScrollManager';

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '100%',
        minHeight: '100vh',
        backgroundColor: tokens.colorNeutralBackground2,
    },
    header: {
        position: 'relative',
        height: '200px',
        '@media (min-width: 768px)': {
            height: '300px',
        },
    },
    bannerImage: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        backgroundColor: tokens.colorNeutralBackground3,
    },
    headerOverlay: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.9) 100%)',
        padding: tokens.spacingVerticalL,
        color: 'white',
    },
    headerContent: {
        maxWidth: '1200px',
        margin: '0 auto',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalM,
        '@media (min-width: 768px)': {
            flexDirection: 'row',
            alignItems: 'flex-end',
            gap: tokens.spacingHorizontalXL,
        },
    },
    titles: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
    },
    stats: {
        display: 'flex',
        gap: tokens.spacingHorizontalS,
        flexWrap: 'wrap',
        padding: tokens.spacingVerticalXS,
        backdropFilter: 'blur(8px)',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderRadius: tokens.borderRadiusMedium,
        '@media (min-width: 768px)': {
            padding: tokens.spacingVerticalS,
        },
    },
    mediaContent: {
        position: 'relative',
        padding: tokens.spacingVerticalM,
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,
        '@media (min-width: 768px)': {
            padding: tokens.spacingVerticalXL,
            paddingLeft: tokens.spacingHorizontalXXL,
            paddingRight: tokens.spacingHorizontalXXL,
        },
    },
    details: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalM,
    },
    titleSection: {
        display: 'none', // Hide romaji title on mobile
        '@media (min-width: 768px)': {
            display: 'flex',
            flexDirection: 'column',
            gap: tokens.spacingVerticalXS,
        },
    },
    statBadge: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalXS,
    },
    sectionContent: {
        backgroundColor: tokens.colorNeutralBackground1,
        padding: tokens.spacingVerticalM,
        borderRadius: tokens.borderRadiusMedium,
        '@media (min-width: 768px)': {
            padding: tokens.spacingVerticalL,
        },
    },
    charactersGrid: {
        marginTop: tokens.spacingVerticalM,
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
        gap: tokens.spacingHorizontalM,
    },
    characterCard: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
        padding: tokens.spacingVerticalS,
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusSmall,
        transition: 'all 0.2s ease',
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground3,
        },
    },
    characterHeader: {
        display: 'flex',
        gap: tokens.spacingHorizontalM,
        alignItems: 'center',
    },
    characterImage: {
        width: '64px',
        height: '64px',
        borderRadius: tokens.borderRadiusSmall,
        objectFit: 'cover',
    },
    voiceActor: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        padding: tokens.spacingVerticalXS,
        backgroundColor: tokens.colorNeutralBackground1,
        borderRadius: tokens.borderRadiusSmall,
    },
    voiceActorImage: {
        width: '32px',
        height: '32px',
        borderRadius: tokens.borderRadiusSmall,
        objectFit: 'cover',
    },
    icon: {
        color: tokens.colorNeutralForeground3,
    },
    infoSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalM,
    },
    statistics: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: tokens.spacingHorizontalL,
        marginBottom: tokens.spacingVerticalL,
    },
    statCard: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        padding: tokens.spacingVerticalM,
        backgroundColor: tokens.colorNeutralBackground1,
        borderRadius: tokens.borderRadiusMedium,
        boxShadow: tokens.shadow4,
    },
    statValue: {
        color: tokens.colorBrandForeground1,
        fontSize: tokens.fontSizeBase600,
        lineHeight: tokens.lineHeightBase600,
    },
    statLabel: {
        color: tokens.colorNeutralForeground2,
        fontSize: tokens.fontSizeBase200,
    },
    statIcon: {
        fontSize: '24px',
        color: tokens.colorBrandForeground1,
        marginBottom: tokens.spacingVerticalXS,
    },
    description: {
        margin: `${tokens.spacingVerticalM} 0`,
        lineHeight: tokens.lineHeightBase400,
        color: tokens.colorNeutralForeground1,
    },
    genres: {
        display: 'flex',
        gap: tokens.spacingHorizontalXS,
        flexWrap: 'wrap',
    },
    characterInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXXS,
    },
    characterName: {
        color: tokens.colorNeutralForeground1,
    },
    characterRole: {
        color: tokens.colorBrandForeground1,
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightSemibold,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
});

interface MediaPageProps {
    media: UserMedia;
}

export const MediaPage: React.FC<MediaPageProps> = ({ media }) => {
    const styles = useStyles();
    const { saveScrollPosition } = useScrollManager();

    // Helper function to format the episode progress
    const formatEpisodeProgress = (current?: number | null, total?: number | null, userRepeat?: number | null) => {
        const useCurrent = current ?? 0,
            useTotal = total ?? 0,
            useRepeatText = userRepeat ? ` (${userRepeat})` : '';

        return `${useCurrent} / ${useTotal}${useRepeatText}`;
    };

    // Helper function to sanitize and parse HTML
    const sanitizeAndParseHtml = (html: string) => {
        const sanitized = DOMPurify.sanitize(html, {
            ALLOWED_TAGS: ['br', 'p', 'em', 'strong', 'i', 'b'],
            ALLOWED_ATTR: []
        });
        return parse(sanitized);
    };

    // Save scroll position on unmount
    React.useEffect(() => {
        return () => {
            saveScrollPosition();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                {media.bannerImage ? (
                    <img src={media.bannerImage} alt="Banner" className={styles.bannerImage} />
                ) : (
                    <img
                        src={media.coverImage.large}
                        alt={media.title.english || media.title.romaji || 'Cover'}
                        className={styles.bannerImage}
                    />
                )}
                <div className={styles.headerOverlay}>
                    <div className={styles.headerContent}>
                        <div className={styles.titles}>
                            <Title1>{media.title.english || media.title.romaji}</Title1>
                            {media.title.english && media.title.romaji && (
                                <Text
                                    size={300}
                                    weight="medium"
                                    style={{ color: 'rgba(255, 255, 255, 0.9)' }}
                                >
                                    {media.title.romaji}
                                </Text>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className={styles.mediaContent}>
                <div className={styles.infoSection}>
                    {/* Statistics Section */}
                    <div className={styles.statistics}>
                        {media.userScore !== null && (
                            <div className={styles.statCard}>
                                <Star24Filled className={styles.statIcon} />
                                <Text className={styles.statValue}>{media.userScore}</Text>
                                <Text className={styles.statLabel}>Your Score</Text>
                            </div>
                        )}
                        {media.averageScore && (
                            <div className={styles.statCard}>
                                <Star24Filled className={styles.statIcon} />
                                <Text className={styles.statValue}>{media.averageScore}%</Text>
                                <Text className={styles.statLabel}>Average Score</Text>
                            </div>
                        )}
                        {(media.userProgress || media.episodes) && (
                            <div className={styles.statCard}>
                                <Play24Filled className={styles.statIcon} />
                                <Text className={styles.statValue}>
                                    {formatEpisodeProgress(media.userProgress, media.episodes, media.userRepeat)}
                                </Text>
                                <Text className={styles.statLabel}>Progress</Text>
                            </div>
                        )}
                        {media.popularity && (
                            <div className={styles.statCard}>
                                <Person24Filled className={styles.statIcon} />
                                <Text className={styles.statValue}>{media.popularity.toLocaleString()}</Text>
                                <Text className={styles.statLabel}>Popularity</Text>
                            </div>
                        )}
                    </div>

                    {/* Genres Section */}
                    {media.genres && media.genres.length > 0 && (
                        <div className={styles.sectionContent}>
                            <Title3>Genres</Title3>
                            <div className={styles.genres}>
                                {media.genres.map(genre => (
                                    <Badge
                                        key={genre}
                                        appearance="filled"
                                        style={{ backgroundColor: tokens.colorBrandBackground2 }}
                                    >
                                        {genre}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Description Section */}
                    {media.description && (
                        <div className={styles.sectionContent}>
                            <Title3>Description</Title3>
                            <Text block className={styles.description}>
                                {sanitizeAndParseHtml(media.description)}
                            </Text>
                        </div>
                    )}

                    {/* Characters Section */}
                    {(media.characters?.length ?? 0) > 0 && (
                        <div className={styles.sectionContent}>
                            <Title3>Characters & Voice Actors</Title3>
                            <div className={styles.charactersGrid}>
                                {media.characters.map(character => (
                                    <Card key={character.id} className={styles.characterCard}>
                                        <div className={styles.characterHeader}>
                                            {character.imageUrl ? (
                                                <img
                                                    src={character.imageUrl}
                                                    alt={character.name}
                                                    className={styles.characterImage}
                                                />
                                            ) : (
                                                <Person24Regular className={styles.icon} />
                                            )}
                                            <div className={styles.characterInfo}>
                                                <Text className={styles.characterName} weight="semibold">
                                                    {character.name}
                                                </Text>
                                                {character.role && (
                                                    <Text className={styles.characterRole}>
                                                        {character.role}
                                                    </Text>
                                                )}
                                            </div>
                                        </div>
                                        {character.voiceActor && (
                                            <Link to={`/actor/${character.voiceActor.id}`} style={{ textDecoration: 'none' }}>
                                                <div className={styles.voiceActor}>
                                                    {character.voiceActor.imageUrl ? (
                                                        <img
                                                            src={character.voiceActor.imageUrl}
                                                            alt={character.voiceActor.name ?? ""}
                                                            className={styles.voiceActorImage}
                                                        />
                                                    ) : (
                                                        <Mic24Regular className={styles.icon} />
                                                    )}
                                                    <Text size={200}>
                                                        {character.voiceActor.name}
                                                    </Text>
                                                </div>
                                            </Link>
                                        )}
                                    </Card>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};